import { InflateRequestMode } from '@sccs/types';
import { intersect, pipe, rawTransform } from 'valibot';
import { processOneSearchCharacteristics } from '../../technicalData/onesearch/characteristics';
import { processOneSearchConsumption } from '../../technicalData/onesearch/consumptions';
import {
  ParsedTechnicalData,
  technicalDataParserOneSearch,
} from '../../technicalData/onesearch/TechnicalDataOneSearch';
import { MixedOneSearchGraphQlResponse } from '../../types/one-search/onesearch-response/OneSearchGraphQlResponse';
import { Vehicle } from '../../types/one-search/queries';
import { appendUserContextToUrl } from '../../util/EnhanceUrlWithCtx';
import { generatePrices } from '../helper/prices';
import {
  Availability,
  Badge,
  CharacteristicsVehicleTileData,
  Common,
  Consumptions,
  Cta,
  Description,
  EcoRatingVehicleTileData,
  Heading,
  Highlight,
  Highlights,
  ImageVehicleTileData,
  MatchRate,
  OnlineCode,
  Prices,
  SchemaAny,
  Tag,
} from '../VehicleTileDataSchema';

export const OneSearchGraphQLTechnicalDataTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Partial<ParsedTechnicalData>>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchTechnicalDataTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    try {
      const { lang, market, translations, ...rest } = dataset.value;
      const parser = technicalDataParserOneSearch(rest, market, lang, dataset.value.mode, translations);
      return parser.getTechnicalData();
    } catch (error) {
      addIssue({
        message: 'Technical data parsing failed',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      throw error;
    }
  }),
);

export const OneSearchGraphQlCommonTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Common>(({ dataset, addIssue, NEVER }) => {
    if (dataset.value === null) {
      addIssue({
        message: 'Dataset value is null. Please provide a valid object.',
        path: [{ type: 'object', origin: 'value', input: dataset.value, key: 'value', value: dataset.value }],
      });
      return NEVER;
    }
    return {
      common: {
        hasCriticalError: false,
        brand: dataset.value.brand,
        productType: 'STOCK',
        bm6: dataset.value?.vehicleModel?.baumuster as string,
        modelIdentifier: dataset.value?.vehicleModel?.vehicleClass?.value as string,
        bodyType: dataset.value?.vehicleModel?.bodyType?.value as string,
        vehicleName: dataset.value?.vehicleModel?.name as string,
        identification: {
          commissionNumber: dataset.value.identification?.commissionNumber as string,
          productCode: dataset.value.identification?.code as string,
          configurationId: undefined,
        },
        isVan: dataset.value.vehicleModel?.category?.value === 'PC' ? false : true,
      },
    };
  }),
);

export const OneSearchGraphQlTagTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Tag>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for OneSearchPreferenceOfferTeaserDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const badgesArray: Badge[] =
      dataset.value.characteristics?.campaigns
        ?.filter((badge) => Boolean(badge?.label))
        .map((badge) => {
          if (!badge?.label) {
            addIssue({
              message: 'label of badge is not set',
              path: [
                {
                  type: 'object',
                  origin: 'value',
                  input: dataset.value as Record<string, unknown>,
                  key: 'value',
                  value: dataset.value,
                },
              ],
            });
            return NEVER;
          }
          return {
            name: badge?.label,
            description: badge?.description ?? undefined,
            footnote: badge?.footnote ?? undefined,
          };
        }) ?? [];

    const badgesSet = [
      {
        showIcon: true,
        badges: badgesArray,
      },
    ];

    return {
      tag: {
        badges: badgesSet,
      },
    };
  }),
);

export const OneSearchGraphQlImageTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, ImageVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for OneSearchPreferenceOfferTeaserDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    if (!dataset.value.images) {
      addIssue({
        message: 'Image URL is not defined',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return {
        image: {
          url: undefined,
          alt: undefined,
          title: undefined,
        },
      };
    }

    return {
      image: {
        url: dataset.value.images.default as string,
        alt: undefined,
        title: undefined,
      },
    };
  }),
);

export const OneSearchGraphQlHeadingTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Heading>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for ProductDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const headingContent = dataset.value.vehicleModel?.name as string;
    if (!headingContent) {
      addIssue({
        message: 'The Heading is not defined',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
    }

    return {
      heading: {
        content: headingContent,
      },
    };
  }),
);
export const OneSearchGraphQlCharacteristicsTransformer = pipe(
  SchemaAny,
  OneSearchGraphQLTechnicalDataTransformer,
  rawTransform<Partial<ParsedTechnicalData>, CharacteristicsVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OfferTeaserDataDtoCharacteristicsTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      characteristics: processOneSearchCharacteristics(dataset.value),
    };
  }),
);

export const OneSearchGraphQlPricesTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Prices>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchPreferencePricesTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    if (!dataset?.value?.identification?.dcpProductType) {
      addIssue({
        message: 'dcpProductType is not available',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    return {
      prices: generatePrices({
        productCode: dataset.value.identification.code as string,
        commissionNumber: dataset.value.identification.commissionNumber as string,
        dcpProductType: dataset.value?.identification.dcpProductType as string,
        productType: 'STOCK',
        market: dataset.value.market,
        locale: dataset.value.lang,
        currency: undefined, // TODO: Do we need this data from OneSearch? Unsure, works in MY
        priceRawValue: undefined, // TODO: Do we need this data from OneSearch? Unsure, works in MY
      }),
    };
  }),
);

export const OneSearchGraphQlHighlightTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Highlights>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OfferTeaserDataHighlightTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const highlights: Highlight[] = [];
    if (dataset.value?.characteristics?.highlights) {
      for (const highlight of dataset.value.characteristics.highlights) {
        highlights.push({ content: highlight.label as string });
      }
    }
    return {
      highlights,
    };
  }),
);

export const OneSearchGraphQlConsumptionTransformer = pipe(
  SchemaAny,
  OneSearchGraphQLTechnicalDataTransformer,
  rawTransform<Partial<ParsedTechnicalData>, Consumptions>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OfferTeaserDataDtoConsumptionTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      consumption: processOneSearchConsumption(dataset.value),
    };
  }),
);

export const OneSearchGraphQlEcoRatingTransformer = pipe(
  SchemaAny,
  OneSearchGraphQLTechnicalDataTransformer,
  rawTransform<Partial<ParsedTechnicalData>, EcoRatingVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchPreferenceEcoRatingTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      ecoRating: {
        image: dataset.value.ecoRating?.image,
      },
    };
  }),
);

export const OneSearchGraphQlCtaTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Cta>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchPreferenceCtaTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const pdpLink = dataset.value.pdpLink as string;
    const srpLink = dataset.value.url ?? (dataset.value?.srpLink as string);
    if (
      dataset.value.mode === InflateRequestMode.SIMPLE_PRICE ||
      dataset.value.mode === InflateRequestMode.BANNER_CAMPAIGN
    ) {
      if (!srpLink) {
        addIssue({
          message: 'The SRP Link is not provided, but is important for the Teaser',
          path: [
            {
              type: 'object',
              origin: 'value',
              input: dataset.value as Record<string, unknown>,
              key: 'value',
              value: dataset.value,
            },
          ],
        });
        return NEVER;
      }
    } else {
      if (!pdpLink) {
        addIssue({
          message: 'The PDP Link is not provided, but is important for the Teaser',
          path: [
            {
              type: 'object',
              origin: 'value',
              input: dataset.value as Record<string, unknown>,
              key: 'value',
              value: dataset.value,
            },
          ],
        });
        return NEVER;
      }
    }
    return {
      cta: {
        pdp: appendUserContextToUrl(pdpLink, dataset.value.ctx),
        srp: appendUserContextToUrl(srpLink, dataset.value.ctx),
      },
    };
  }),
);

export const OneSearchGraphQlOnlineCodeTransformer = pipe(
  SchemaAny,
  rawTransform<Vehicle, OnlineCode>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OfferTeaserDtoOnlineCodeTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      content: undefined,
    };
  }),
);

export const OneSearchGraphQlAvailabilityTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Availability>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issue in OfferTeaserDtoAvailabilityTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      status: undefined,
      estimatedArrivalDate: dataset.value.estimatedArrivalDate,
    };
  }),
);

export const OneSearchGraphQlMatchRateTransformer = pipe(
  SchemaAny,
  rawTransform<Vehicle, MatchRate>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OneSearchPreferenceMatchRateTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      rate: undefined,
    };
  }),
);

export const OneSearchGraphQLDescriptionTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchGraphQlResponse, Description>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in OfferTeaserDataDtoDescriptionTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      description: {
        content: undefined,
      },
    };
  }),
);

export const ConvertOneSearchGraphQlToVehicleDataTransformer = intersect([
  OneSearchGraphQlCommonTransformer,
  OneSearchGraphQlTagTransformer,
  OneSearchGraphQlImageTransformer,
  OneSearchGraphQlHeadingTransformer,
  OneSearchGraphQlCharacteristicsTransformer,
  OneSearchGraphQlPricesTransformer,
  OneSearchGraphQlHighlightTransformer,
  OneSearchGraphQlConsumptionTransformer,
  OneSearchGraphQlEcoRatingTransformer,
  OneSearchGraphQlCtaTransformer,
]);
